import { isRejected, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { createApi, FetchArgs, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import {
  cancelRefreshTimer,
  clearTokens,
  getAccessToken,
  getGuestSessionId,
  getTgUserData,
  isTelegramWebApp,
  setGuestSessionId,
} from '@/shared/lib';

export const baseUrl = import.meta.env.VITE_API_URL;

const TIMEOUT = 15 * 1000;
const MAX_RETRIES = 3;

export const getChainDomain = (): string | null => {
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_CHAIN_DOMAIN) return import.meta.env.VITE_DEV_CHAIN_DOMAIN;

  return window.location.hostname;
};

const fetchWithTimeoutAndRetry = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const result = await fetchBaseQuery({
      baseUrl,
      timeout: TIMEOUT,
      prepareHeaders: (headers, { getState, endpoint }) => {
        const isTgApp = isTelegramWebApp();
        // @ts-expect-error: state shape not in RootState here
        const currentChainId = getState()?.initial?.chainId;
        if (currentChainId && isTgApp) headers.set('X-Chain-Id', currentChainId);

        const chainDomain = getChainDomain();
        if (chainDomain && !isTgApp) headers.set('X-chain-domain', chainDomain);

        const guestSessionId = getGuestSessionId();
        if (guestSessionId) headers.set('x-guest-session-id', guestSessionId);

        if (endpoint === 'authTelegramLogin') {
          const initData = getTgUserData()?.initData;
          if (initData) headers.set('telegram-web-app-init-data', initData);
          return headers;
        }

        if (endpoint === 'authRefresh') {
          return headers;
        }

        const token = getAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);

        return headers;
      },
    })(args, api, extraOptions);

    const guestSessionId = result.meta?.response?.headers.get('x-guest-session-id');
    if (guestSessionId) setGuestSessionId(guestSessionId);

    if (result.error && result.error?.status !== 'TIMEOUT_ERROR') retry.fail(result.error, result.meta);

    return result;
  },
  { maxRetries: MAX_RETRIES },
);

export const emptyApi = createApi({
  reducerPath: 'api',
  endpoints: () => ({}),
  baseQuery: fetchWithTimeoutAndRetry,
  tagTypes: ['Categories', 'MenuItems', 'Orders', 'Locations', 'Promocodes'],
});

export const rtkQueryErrorLogger: Middleware =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_: MiddlewareAPI) => (next) => (action) => {
    // isRejectedWithValue Or isRejected
    if (isRejected(action)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((action as any).payload?.status === 401) {
        clearTokens();
        cancelRefreshTimer();
      }
    }

    return next(action);
  };
